import { PageController } from '@client/components/_core/layout/PageController';
import { useOrgSettingsStore } from '@client/stores/OrgSettingStore';
import { PUBLIC_READINESS_QUERY_META } from '@client/trpc/query-meta';
import { ORG_SETTINGS_QUERY_OPTIONS } from '@client/trpc/query-options';
import type { ShareableRouteContext } from '@client/types/router';
import * as Sentry from '@sentry/react';
import { SYSTEM_ORG_IDS } from '@shared/constants';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { Spin } from 'antd';
import { useEffect, useState } from 'react';
import { z } from 'zod';

export const Route = createFileRoute('/public/$orgId')({
    params: {
        parse: (params) => {
            return { orgId: z.coerce.bigint().parse(params.orgId) };
        },
    },
    beforeLoad: ({ context, params }): ShareableRouteContext => {
        // Every /public/$orgId page blanks its outlet until `OrgSettingStore.init`
        // resolves, and init runs in a mount effect — the fetch used to start only
        // after the full JS boot. Start it at the router instead; `init` fetches
        // through the same tRPC cache with the same staleTime, so it picks this up
        // without a second request (CUR-1036, task 2.1). Public procedure — no auth
        // gating; fire-and-forget (failures fall through to init's own handling).
        if (params.orgId >= SYSTEM_ORG_IDS.FIRST_VALID_ORG_ID) {
            void context.trpcUtils.org.getOrgSettings.prefetch(
                { orgId: params.orgId },
                { ...ORG_SETTINGS_QUERY_OPTIONS, retry: false, meta: PUBLIC_READINESS_QUERY_META },
            );
        }
        return undefined;
    },
    component: PublicOrgSectionLayout,
});

function PublicOrgSectionLayout() {
    const orgSettingsStore = useOrgSettingsStore();
    const { orgId } = Route.useParams();
    const [isInitialized, setIsInitialized] = useState(false);

    useEffect(() => {
        const asyncWrapper = async () => {
            if (orgId < SYSTEM_ORG_IDS.FIRST_VALID_ORG_ID) {
                return;
            }

            try {
                await orgSettingsStore.init(orgId, { queryMeta: PUBLIC_READINESS_QUERY_META });
                setIsInitialized(true);
            } catch (error) {
                Sentry.captureException(error, { tags: { location: 'PublicOrgSection' } });
            }
        };

        void asyncWrapper();
    }, [orgId]);

    return (
        <PageController route={Route}>
            {isInitialized ? (
                <Outlet />
            ) : (
                <div className="flex h-full w-full items-center justify-center">
                    <Spin spinning size="large" />
                </div>
            )}
        </PageController>
    );
}
